import type { Task } from "@app/core";
import type { DayCol } from "./build-days.js";

export const EMPTY: Task[] = [];
export const EMPTY_OVERFLOW: Array<{ task: Task; overflowMinutes: number }> = [];

const DAY_MIN = 24 * 60;

/**
 * Derives per-column render data from the stable day window so a pan doesn't
 * rebuild it. `timedByCol` is each day's pre-filtered timed list;
 * `overflowByCol` carries multi-day spills into later columns; `allDayTaskMap`
 * groups all-day/undated tasks by id with their start/end column for the span
 * row.
 */
export function buildColumns(days: DayCol[]) {
  const timedByCol = days.map((d) => d.timed);
  const overflowBuckets: Array<Array<{ task: Task; overflowMinutes: number }> | undefined> = Array.from({ length: days.length });

  for (let col = 0; col < timedByCol.length; col++) {
    for (const task of timedByCol[col] ?? []) {
      if (!task.due_at) continue;
      const start = new Date(task.due_at);
      const startMin = start.getHours() * 60 + start.getMinutes();
      let overflowMinutes = task.duration_minutes - (DAY_MIN - startMin);

      for (let nextCol = col + 1; nextCol < days.length && overflowMinutes > 0; nextCol++) {
        (overflowBuckets[nextCol] ??= []).push({ task, overflowMinutes: Math.min(overflowMinutes, DAY_MIN) });
        overflowMinutes -= DAY_MIN;
      }
    }
  }

  // Timed tasks that START before the window but spill continuation segments
  // into it: bucketed on their continuation days (never in a `timed` start-day
  // list, so the scan above can't see them) — synthesize their chain from day 0.
  const first = days[0];
  if (first) {
    const base = first.date.getTime();
    for (const task of first.tasks) {
      if (task.all_day || !task.due_at) continue;
      const start = new Date(task.due_at);
      const startDay = new Date(start);
      startDay.setHours(0, 0, 0, 0);
      const daysBefore = Math.round((base - startDay.getTime()) / 86_400_000);
      if (daysBefore <= 0) continue; // starts in-window — handled above
      const startMin = start.getHours() * 60 + start.getMinutes();
      let overflowMinutes = task.duration_minutes - (DAY_MIN - startMin) - (daysBefore - 1) * DAY_MIN;
      for (let col = 0; col < days.length && overflowMinutes > 0; col++) {
        (overflowBuckets[col] ??= []).push({ task, overflowMinutes: Math.min(overflowMinutes, DAY_MIN) });
        overflowMinutes -= DAY_MIN;
      }
    }
  }

  // Shared empty ref for the common no-overflow case: memoized columns skip.
  const overflowByCol = overflowBuckets.map((bucket) => bucket ?? EMPTY_OVERFLOW);

  const allDayTaskMap = new Map<string, { task: Task; startCol: number; endCol: number }>();
  for (let i = 0; i < days.length; i++) {
    for (const task of days[i]?.tasks ?? []) {
      if (!task.all_day && task.due_at) continue;
      const existing = allDayTaskMap.get(task.id);
      if (!existing) allDayTaskMap.set(task.id, { task, startCol: i, endCol: i });
      else if (i > existing.endCol) existing.endCol = i;
    }
  }

  return { timedByCol, overflowByCol, allDayTaskMap };
}
